import type { ConflictingAppointment } from '../wire';
import type { OutboxEntry } from './entry';

/**
 * A rejected intent, turned into something the owner can act on (spec §8.3).
 *
 * The spec writes the sentence out, and it is worth reading twice for what it
 * contains:
 *
 * > *"This 2 PM was booked by the concierge while you were offline. Reschedule
 * > Dana?"*
 *
 * Three facts, none of which a generic error message has: **when** the collision
 * was (2 PM), **who took it** (the concierge — that is `conflicts[].source ===
 * 'partner'`), and **who is left without an appointment** (Dana, from the intent
 * the device queued). `enrichConflict` in `core-api` exists to supply the middle
 * one; the outbox's `describe` snapshot supplies the third. This file is the only
 * place the three meet, so the copy is one thing to get right rather than one per
 * view.
 *
 * The honesty rule: when a fact is missing, the sentence loses that clause
 * rather than inventing it. "Someone else has this time" is a worse sentence
 * than the spec's and a better one than a guess.
 */

export interface OwnerTask {
  /** The rejected outbox entry's id. */
  id: string;
  kind: OutboxEntry['kind'];
  createdAt: string;
  /** One line, the heading of the task card. */
  title: string;
  /** The sentence. Ends in a question when there is something to decide. */
  message: string;
  /** What the collision was, when the server told us. */
  conflicts: ConflictingAppointment[];
  /** True when "Reschedule" is a meaningful button. */
  canReschedule: boolean;
  /** Pre-fills the create flow when the owner taps Reschedule. */
  prefill?: ReschedulePrefill | undefined;
}

export interface ReschedulePrefill {
  serviceId?: string | undefined;
  resourceId?: string | undefined;
  clientId?: string | undefined;
  guestName?: string | undefined;
  startIso?: string | undefined;
}

export interface TaskCopyOptions {
  timezone: string;
}

export function taskFor(entry: OutboxEntry, options: TaskCopyOptions): OwnerTask {
  const rejection = entry.rejection;
  const conflicts = rejection?.conflicts ?? [];
  const base = {
    id: entry.id,
    kind: entry.kind,
    createdAt: entry.createdAt,
    conflicts,
  };

  if (rejection?.code === 'conflict' && rejection.detail?.['reason'] === 'slot_taken') {
    return {
      ...base,
      title: `${entry.describe.what} did not go through`,
      message: slotTakenMessage(entry, conflicts, options),
      canReschedule: entry.kind === 'appointment.create' || entry.kind === 'appointment.reschedule',
      prefill: prefillFrom(entry),
    };
  }

  return {
    ...base,
    title: `${entry.describe.what} did not go through`,
    message: plainMessage(entry),
    canReschedule: false,
  };
}

/**
 * The sentence, assembled from what is actually known.
 *
 * `source` decides the middle clause and nothing else does. A booking the
 * partner's agent sold and a booking the owner made on the other iPad are the
 * same 409 and very different news, and the spec's own argument for the whole
 * model — *"the alternative is a device that can silently create a booking the
 * partner has already sold"* — is only visible to the owner if the task says
 * which happened.
 */
function slotTakenMessage(
  entry: OutboxEntry,
  conflicts: ConflictingAppointment[],
  options: TaskCopyOptions,
): string {
  const when = formatTime(entry.describe.whenIso, options.timezone);
  const winner = conflicts[0];
  const subject = when === null ? 'That time' : `This ${when}`;

  const taker = winner === undefined ? null : takerClause(winner);
  const opening =
    taker === null
      ? `${subject} was taken while you were offline.`
      : `${subject} was ${taker} while you were offline.`;

  const person = entry.describe.person;
  if (person === undefined) return `${opening} Pick another time?`;
  return entry.kind === 'appointment.create'
    ? `${opening} Rebook ${person}?`
    : `${opening} Reschedule ${person}?`;
}

function takerClause(conflict: ConflictingAppointment): string {
  const who = conflict.client_name;
  const tail = who === null ? '' : ` for ${who}`;
  switch (conflict.source) {
    case 'partner':
      return `booked by the concierge${tail}`;
    case 'walk_in':
      return `given to a walk-in${tail}`;
    case 'import':
      return `already in the imported book${tail}`;
    case 'staff':
      return `booked at the counter${tail}`;
  }
}

function plainMessage(entry: OutboxEntry): string {
  const rejection = entry.rejection;
  if (rejection === undefined) return 'This did not go through.';
  switch (rejection.code) {
    case 'not_found':
      return `${rejection.message} It may have been cancelled or deleted while you were offline.`;
    case 'forbidden':
      return `${rejection.message} This device is signed in to a different business than the one this change was for.`;
    case 'conflict':
      return rejection.message;
    case 'unsupported':
      return `${rejection.message} That part of the app is switched off for this business.`;
    default:
      return rejection.message;
  }
}

function prefillFrom(entry: OutboxEntry): ReschedulePrefill {
  const payload = entry.payload;
  return {
    serviceId: stringOrUndefined(payload['service_id']),
    resourceId: stringOrUndefined(payload['resource_id']),
    clientId: stringOrUndefined(payload['client_id']) ?? entry.target['client_id'],
    guestName: entry.describe.person,
    startIso: entry.describe.whenIso,
  };
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/** "2 PM", "2:30 PM" — the spec's own phrasing, in the tenant's timezone. */
export function formatTime(iso: string | undefined, timezone: string): string | null {
  if (iso === undefined) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(at);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const minute = get('minute');
  const meridiem = get('dayPeriod').toUpperCase();
  return minute === '00' ? `${get('hour')} ${meridiem}` : `${get('hour')}:${minute} ${meridiem}`;
}
